import { OnDestroy, Pipe, PipeTransform } from '@angular/core';

// Converts a base64 `data:` URL to a `blob:` object URL. The binary lives in
// browser-managed memory once and the `<img>` carries only a short opaque
// reference — keeps the DOM small and avoids re-serializing the data URL on
// every change detection cycle. Any other URL yields `undefined` rather than
// being passed through: inputs can come from peers (or messages persisted
// before receivers validated them), and a remote URL would be fetched by
// every viewer.
//
// Pure pipes are stateful per template usage: each `| blobUrl` in a template
// instantiates one pipe object whose lifecycle is tied to the host view. We
// rely on that to revoke the URL when the view is torn down (message pruned,
// scrolled out, navigated away).
function dataUrlToBlob(dataUrl: string): Blob {
  const commaIdx = dataUrl.indexOf(',');
  const header = dataUrl.slice(0, commaIdx);
  const body = dataUrl.slice(commaIdx + 1);
  const mime = header.match(/data:([^;]+)/)?.[1] ?? 'application/octet-stream';
  const binary = atob(body);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

@Pipe({ name: 'blobUrl', standalone: true })
export class BlobUrlPipe implements PipeTransform, OnDestroy {
  private _url?: string;
  private _lastInput?: string;

  transform(value: string | undefined | null): string | undefined | null {
    if (!value) return value;
    if (!value.startsWith('data:')) return undefined;
    if (value === this._lastInput && this._url) return this._url;
    if (this._url) URL.revokeObjectURL(this._url);
    this._lastInput = value;
    try {
      this._url = URL.createObjectURL(dataUrlToBlob(value));
      return this._url;
    } catch {
      this._url = undefined;
      return value;
    }
  }

  ngOnDestroy(): void {
    if (this._url) URL.revokeObjectURL(this._url);
  }
}
